import { Alert, Divider, FormGroup, Heading, TextArea } from '@ksyos/design-system';
import { useEffect } from 'react';
import type { tcohV2Advies } from '@common/customControls/TCOH/v2/tcohV2Advies';
import type { AdviesConsult } from '@common/interfaces/adviesConsult';
import type { IServiceGetOne } from '@frontend/components/interfaces';
import type { ValidationErrors } from '@frontend/utils';
import { maxOverigeOpmerkingenLength } from '@common/validation/customControls/cardiology/holterConsult/cardiology';
import { nullDiagnosis } from '@common/validation/diagnosis';
import { FormParagraph } from '@frontend/components/elements/common';
import { FormLabel, HorizontalFormGroupRead, InputContainer } from '@frontend/components/elements/forms';
import { TypeaheadInput } from '@frontend/components/fields';
import { getSelectedLanguage } from '@frontend/translate';
import { getValidationErrorTranslation } from '@frontend/utils';
import { Icd10Selections } from '../../utils/v1/Icd10/Icd10Selections';

export const emptyAdviesConsult = {
    advies: null,
    diagnosis: { ...nullDiagnosis },
    overigeOpmerkingen: null,
} as const;

type AdviesConsultAddEditProps = {
    adviesConsult: AdviesConsult;
    adviesOptions: tcohV2Advies[];
    errors: ValidationErrors;
    onChange: (value: AdviesConsult) => void;
    service: IServiceGetOne;
    validateOneCustomControl: (name: string) => Promise<void>;
    vraag: string;
};

export function AdviesConsultAddEdit(props: Readonly<AdviesConsultAddEditProps>) {
    const translate = getSelectedLanguage();

    useEffect(() => {
        if (props.adviesConsult.advies === null && props.adviesOptions.length === 1) {
            props.onChange({ ...props.adviesConsult, advies: props.adviesOptions[0] });
        }
    }, []);

    return (
        <>
            <Divider />
            <Heading size="small">Advies</Heading>

            <Alert appearance="info">
                Let op: de huisarts heeft aangegeven dat advies gewenst is bij een afwijkende conclusie
            </Alert>

            <HorizontalFormGroupRead>
                <FormLabel>Vraag</FormLabel>
                <InputContainer>
                    <FormParagraph>{props.vraag}</FormParagraph>
                </InputContainer>
            </HorizontalFormGroupRead>

            <Icd10Selections
                diagnosis={props.adviesConsult.diagnosis}
                errors={props.errors}
                isWorkingDiagnosis={false}
                onChange={async (diagnosis) => props.onChange({ ...props.adviesConsult, diagnosis })}
                onDoValidate={() => props.validateOneCustomControl('adviesConsult.diagnosis')}
                placeholder="Typ en kies je diagnose"
                specialismCode={props.service.specialismCode!}
            />

            <FormGroup
                hasErrors={props.errors.has('customControlData.adviesConsult.advies')}
                isRequired={true}
                label={translate.diagnosticReport.advice}
            >
                <TypeaheadInput
                    error={props.errors.get('customControlData.adviesConsult.advies')}
                    onChange={async (value) => props.onChange({ ...props.adviesConsult, advies: value })}
                    onDoValidate={() => props.validateOneCustomControl('adviesConsult.advies')}
                    options={props.adviesOptions}
                    placeholder={translate.common.describeOrChooseFromList}
                    value={props.adviesConsult.advies}
                    variant={'outline'}
                />
            </FormGroup>

            <FormGroup
                hasErrors={props.errors.has('customControlData.adviesConsult.overigeOpmerkingen')}
                isRequired={true}
                label={translate.diagnosticReport.additionalRemarks}
            >
                <TextArea
                    error={getValidationErrorTranslation(
                        props.errors.get('customControlData.adviesConsult.overigeOpmerkingen')
                    )}
                    maxLength={maxOverigeOpmerkingenLength}
                    onBlur={() => props.validateOneCustomControl('adviesConsult.overigeOpmerkingen')}
                    onChange={(e) => props.onChange({ ...props.adviesConsult, overigeOpmerkingen: e.target.value })}
                    placeholder={`${translate.diagnosticReport.additionalRemarks}...`}
                    rows={4}
                    value={props.adviesConsult.overigeOpmerkingen ?? ''}
                    variant="outline"
                />
            </FormGroup>
        </>
    );
}
